<template>
  <app-page ref="pageRoot" labelledby="workspace-settings-title">
    <page-header
      title-id="workspace-settings-title"
      title="Настройки рабочего пространства"
      description="Настройте виджеты и внешний вид интерфейса для этого браузера."
    />

    <app-state
      v-if="pageState === 'loading' || pageState === 'error'"
      :state="pageState"
      :message="loadErrorMessage"
      @retry="loadSettings"
    />

    <q-form v-else ref="settingsForm" class="workspace-settings" @submit.prevent="saveSettings">
      <form-section
        title="Виджеты главной страницы"
        description="Выберите блоки, которые должны быть видны после входа."
        :disabled="actionState === 'saving'"
      >
        <form-grid>
          <q-toggle
            v-model="form.department_widget"
            dense
            label="Показывать виджет структуры организации"
          />
          <q-toggle
            v-model="form.workers_widget"
            dense
            label="Показывать виджет списка пользователей"
          />
          <q-toggle
            v-model="form.transfers_widget"
            dense
            label="Показывать виджет неподтвержденных проводок"
          />
        </form-grid>
      </form-section>

      <form-section
        title="Внешний вид"
        description="Темная тема экспериментальная: отдельные старые экраны пока используют светлые поверхности."
        :disabled="actionState === 'saving'"
      >
        <form-grid :default-span="12">
          <q-toggle v-model="form.dark_mode" dense label="Использовать темную тему" />
          <q-input
            v-if="fixture.validation"
            v-bind="controlProps"
            v-model="form.validation_code"
            class="form-grid__full"
            label="Контрольное поле"
            hint="Development-only поле для проверки общего validation contract"
            lazy-rules
            :rules="validationRules"
          />
        </form-grid>
      </form-section>

      <sticky-action-bar
        :state="actionState"
        :dirty="isDirty"
        :error-message="saveErrorMessage"
        @cancel="resetChanges"
        @primary="saveSettings"
      />
    </q-form>

    <confirm-dialog
      :model-value="leaveDialogOpen"
      title="Выйти без сохранения?"
      body="Несохраненные изменения будут потеряны."
      confirm-label="Выйти без сохранения"
      cancel-label="Остаться"
      destructive
      @cancel="cancelDirtyLeave"
      @confirm="confirmDirtyLeave"
      @update:model-value="handleLeaveDialogModel"
    />
  </app-page>
</template>

<script>
import { Dark } from 'quasar'
import {
  AppPage,
  AppState,
  ConfirmDialog,
  FormGrid,
  FormSection,
  PageHeader,
  StickyActionBar
} from '../../../components/ui'
import {
  CANONICAL_CONTROL_PROPS,
  createDirtyLeaveGuard,
  requiredRule,
  validateAndFocus
} from '../../../services/formValidation'

const PROFILE_SETTINGS_KEY = 'profile_settings'
const DEFAULT_SETTINGS = Object.freeze({
  department_widget: true,
  workers_widget: true,
  transfers_widget: true,
  dark_mode: false,
  validation_code: ''
})

function formFixture() {
  if (process.env.NODE_ENV === 'production' || typeof window === 'undefined') return {}
  return window.__SID_E2E_FORM_PRIMITIVES__ || {}
}

function normalizedSettings(value = {}) {
  return {
    department_widget: value.department_widget !== false,
    workers_widget: value.workers_widget !== false,
    transfers_widget: value.transfers_widget !== false,
    dark_mode: value.dark_mode === true,
    validation_code: typeof value.validation_code === 'string' ? value.validation_code : ''
  }
}

function sameSettings(left, right) {
  return Object.keys(DEFAULT_SETTINGS).every((key) => left[key] === right[key])
}

function wait(milliseconds) {
  return new Promise((resolve) => setTimeout(resolve, milliseconds))
}

export default {
  name: 'ProfileSettings',
  components: {
    AppPage,
    AppState,
    ConfirmDialog,
    FormGrid,
    FormSection,
    PageHeader,
    StickyActionBar
  },
  beforeRouteLeave: createDirtyLeaveGuard(),
  data() {
    const fixture = formFixture()
    return {
      actionState: 'idle',
      controlProps: CANONICAL_CONTROL_PROPS,
      fixture,
      form: normalizedSettings(),
      leaveDialogOpen: false,
      loadErrorMessage: '',
      pageState: 'loading',
      pendingNavigation: null,
      saveErrorMessage: '',
      savedForm: normalizedSettings(),
      submitPending: false,
      validationRules: [requiredRule('Заполните контрольное поле')]
    }
  },
  computed: {
    isDirty() {
      return !sameSettings(this.form, this.savedForm)
    }
  },
  watch: {
    form: {
      deep: true,
      handler() {
        if (this.isDirty && (this.actionState === 'saved' || this.actionState === 'error')) {
          this.actionState = 'idle'
          this.saveErrorMessage = ''
        }
      }
    }
  },
  mounted() {
    this.loadSettings()
  },
  methods: {
    async loadSettings() {
      this.pageState = 'loading'
      this.loadErrorMessage = ''
      try {
        if (this.fixture.loadDelay) await wait(this.fixture.loadDelay)
        if (this.fixture.loadError) throw new Error('fixture-load-error')

        const storedProfile = JSON.parse(localStorage.getItem(PROFILE_SETTINGS_KEY) || '{}')
        const storedDepartment = JSON.parse(localStorage.getItem('department_structure_widget') || '{}')
        const loaded = normalizedSettings({
          ...storedProfile,
          dark_mode: Object.prototype.hasOwnProperty.call(storedProfile, 'dark_mode')
            ? storedProfile.dark_mode
            : Dark.isActive,
          department_widget: typeof storedDepartment.is_hidden === 'boolean'
            ? !storedDepartment.is_hidden
            : storedProfile.department_widget
        })
        this.form = { ...loaded }
        this.savedForm = { ...loaded }
        Dark.set(loaded.dark_mode)
        this.pageState = 'ready'
      } catch {
        this.loadErrorMessage = 'Не удалось прочитать сохраненные настройки этого браузера.'
        this.pageState = 'error'
      }
    },
    async saveSettings() {
      if (this.submitPending || this.actionState === 'saving') return
      this.submitPending = true
      const valid = await validateAndFocus(this.$refs.settingsForm, this.$refs.pageRoot)
      if (!valid) {
        this.submitPending = false
        return
      }

      this.actionState = 'saving'
      this.saveErrorMessage = ''
      try {
        if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
          const events = window.__SID_E2E_FORM_EVENTS__ || { saves: 0 }
          events.saves += 1
          window.__SID_E2E_FORM_EVENTS__ = events
        }
        await wait(this.fixture.saveDelay === undefined ? 120 : this.fixture.saveDelay)
        if (this.fixture.saveError) throw new Error('fixture-save-error')

        const nextSettings = normalizedSettings(this.form)
        localStorage.setItem(PROFILE_SETTINGS_KEY, JSON.stringify(nextSettings))
        const departmentSettings = this.$store.state.department_structure_widget || {}
        this.$store.commit('updateDepartmentWidgetSettings', {
          is_hidden: !nextSettings.department_widget,
          horizontal: departmentSettings.horizontal === true
        })
        Dark.set(nextSettings.dark_mode)
        this.savedForm = { ...nextSettings }
        this.form = { ...nextSettings }
        this.actionState = 'saved'
      } catch {
        this.saveErrorMessage = 'Не удалось сохранить настройки. Повторите попытку.'
        this.actionState = 'error'
      } finally {
        this.submitPending = false
      }
    },
    resetChanges() {
      this.form = { ...this.savedForm }
      this.actionState = 'idle'
      this.saveErrorMessage = ''
    },
    requestDirtyLeave(next) {
      this.pendingNavigation = next
      this.leaveDialogOpen = true
    },
    cancelDirtyLeave() {
      const next = this.pendingNavigation
      this.pendingNavigation = null
      this.leaveDialogOpen = false
      if (next) next(false)
    },
    confirmDirtyLeave() {
      const next = this.pendingNavigation
      this.pendingNavigation = null
      this.savedForm = { ...this.form }
      this.leaveDialogOpen = false
      if (next) next()
    },
    handleLeaveDialogModel(value) {
      if (!value && this.leaveDialogOpen) this.cancelDirtyLeave()
    }
  }
}
</script>

<style scoped>
.workspace-settings {
  max-width: 56rem;
}

.form-grid__full {
  --form-grid-span: 12;
  --form-grid-tablet-span: 2;
}
</style>
